import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Check as CheckIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme/tokens';
import { useTelegramLink } from '../../../hooks/useTelegramLink';
import { Lead, Num, StepTitle } from './type';

/**
 * Linking Telegram inside the wizard: the deep link carries a one-time code,
 * so opening it and tapping "Iniciar" is all it takes; the step watches for
 * the link and says so. On a computer, a QR code takes the link to the phone.
 */
const openSx = { alignSelf: { xs: 'stretch', sm: 'flex-start' }, minHeight: { xs: 52, sm: 44 }, fontSize: { xs: 16, sm: 15 } } as const;

const LinkStep: React.FC<{ titleId: string; hideNote?: boolean }> = ({ titleId, hideNote }) => {
  const { t } = useTranslation('onboarding');
  const { isLinked, telegramUsername, deepLink, failed, generate } = useTelegramLink({ auto: true });

  const openButton = isLinked ? null : failed ? (
    <Button variant='outlined' onClick={generate} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
      {t('link.retry')}
    </Button>
  ) : deepLink ? (
    <Button variant='contained' startIcon={<TelegramIcon />} href={deepLink} target='_blank' rel='noopener noreferrer' sx={openSx}>
      {t('link.open')}
    </Button>
  ) : (
    <Button variant='contained' startIcon={<CircularProgress size={16} color='inherit' />} disabled sx={openSx}>
      {t('link.preparing')}
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.25, md: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <StepTitle id={titleId}>{t('link.title')}</StepTitle>
        <Lead>{t('link.lead')}</Lead>
      </Box>

      <Box sx={{ display: 'flex', gap: 3.5, alignItems: 'stretch' }}>
        {/* the QR is for a computer: on the phone, the button opens Telegram right there */}
        {!isLinked && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              width: 280,
              flexShrink: 0,
              p: 2.75,
              borderRadius: '20px',
              bgcolor: tokens.bg,
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              textAlign: 'center',
            }}
          >
            <Box sx={{ p: 1.25, borderRadius: '14px', bgcolor: tokens.surface, width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {deepLink ? (
                <QRCodeSVG value={deepLink} size={180} fgColor={tokens.ink} aria-label={t('link.qrLabel')} role='img' />
              ) : (
                <CircularProgress size={28} aria-label={t('link.preparing')} />
              )}
            </Box>
            <Box component='strong' sx={{ fontSize: 14 }}>{t('link.qrTitle')}</Box>
            <Box component='span' sx={{ fontSize: 13.5, color: tokens.ink3 }}>{t('link.qrText')}</Box>
          </Box>
        )}

        <Box component='ol' sx={{ m: 0, p: { xs: 0, md: '6px 0 0' }, listStyle: 'none', flex: 1, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          <Box component='li' sx={{ display: 'flex', gap: 1.75 }}>
            <Num>1</Num>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <strong>{t('link.step1')}</strong>
              <Box component='span' sx={{ fontSize: 14, color: tokens.ink3 }}>{t('link.step1Text')}</Box>
              {openButton}
              {failed && <Box component='span' role='alert' sx={{ fontSize: 14, color: tokens.warn }}>{t('link.failed')}</Box>}
            </Box>
          </Box>
          <Box component='li' sx={{ display: 'flex', gap: 1.75 }}>
            <Num>2</Num>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <strong>{t('link.step2')}</strong>
              <Box component='span' sx={{ fontSize: 14, color: tokens.ink3 }}>{t('link.step2Text')}</Box>
            </Box>
          </Box>
          <Box component='li' sx={{ display: 'flex', gap: 1.75 }}>
            <Num>3</Num>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <strong>{t('link.step3')}</strong>
              <Box
                role='status'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.75,
                  py: 1.5,
                  borderRadius: '14px',
                  bgcolor: isLinked ? tokens.accentSoft : tokens.bg,
                  fontSize: 14,
                  color: isLinked ? tokens.ink : tokens.ink2,
                }}
              >
                {isLinked ? (
                  <>
                    <CheckIcon aria-hidden sx={{ color: tokens.accent, fontSize: 20 }} />
                    <span>
                      <strong>{t('link.connected')}</strong>
                      {telegramUsername ? ` ${t('link.connectedAs', { username: telegramUsername })}` : ''}. {t('link.connectedHint')}
                    </span>
                  </>
                ) : (
                  <>
                    <CircularProgress size={16} thickness={5} aria-hidden sx={{ color: tokens.ink3 }} />
                    <span>{t('link.waiting')}</span>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {!hideNote && <Box component='p' sx={{ m: 0, fontSize: 13.5, color: tokens.ink3 }}>{t('link.note')}</Box>}
    </Box>
  );
};

export default LinkStep;
